package er.prateek.crossly.config

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The `modes` section of [AppConfig] -- feature flags, difficulty knobs and ad/assist policy for
 * the newer, leveled game modes (math crossword, rapid-fire riddles). Both default **on**, and
 * every field has a default, so a config written by a newer build still parses on an older one
 * (unknown keys ignored, missing keys fall back) and builds that predate the section run on
 * these defaults. See [AppConfig] for how it is fetched/parsed.
 */
@Serializable
data class ModesConfig(
    @SerialName("math_crossword") val mathCrossword: MathCrosswordConfig = MathCrosswordConfig(),
    @SerialName("riddles") val riddles: RiddlesConfig = RiddlesConfig(),
    @SerialName("brainstorm") val brainstorm: BrainstormConfig = BrainstormConfig(),
    @SerialName("daily") val daily: DailyMixConfig = DailyMixConfig(),
) {
    /**
     * Pulls every count into a range the level maps and generators can handle, so a console typo
     * (`0`, `-1`, `100000`) degrades to the nearest sane value instead of an empty map or an
     * instantly-"cleared" zero-question set. Applied once, in [AppConfig.parse].
     */
    fun sanitized(): ModesConfig = copy(
        mathCrossword = mathCrossword.copy(
            levelsPerTier = mathCrossword.levelsPerTier.coerceIn(1, MAX_LEVELS_PER_TIER),
            revealFreePerLevel = mathCrossword.revealFreePerLevel.coerceAtLeast(0),
        ),
        riddles = riddles.copy(
            levelsPerTier = riddles.levelsPerTier.coerceIn(1, MAX_LEVELS_PER_TIER),
            questionsPerLevel = riddles.questionsPerLevel.coerceIn(MIN_QUESTIONS_PER_LEVEL, MAX_QUESTIONS_PER_LEVEL),
            perQuestionSeconds = riddles.perQuestionSeconds.coerceIn(MIN_SECONDS_PER_QUESTION, MAX_SECONDS_PER_QUESTION),
            passRatio = riddles.passRatio.coerceIn(MIN_PASS_RATIO, 1.0),
        ),
        daily = daily.sanitized(),
    )

    companion object {
        val DEFAULT = ModesConfig()

        const val MAX_LEVELS_PER_TIER = 100
        const val MIN_QUESTIONS_PER_LEVEL = 3
        const val MAX_QUESTIONS_PER_LEVEL = 30
        const val MIN_SECONDS_PER_QUESTION = 3
        const val MAX_SECONDS_PER_QUESTION = 120

        /** Below this a set is "cleared" by guessing; at least one right answer is always required anyway. */
        const val MIN_PASS_RATIO = 0.1
    }
}

/** Knobs for the Math Crossword mode. */
@Serializable
data class MathCrosswordConfig(
    @SerialName("enabled") val enabled: Boolean = true,
    /** Number of levels offered per difficulty tier. */
    @SerialName("levels_per_tier") val levelsPerTier: Int = 30,
    /**
     * Fraction of number cells left blank for the player to solve. `0` keeps each tier's own
     * default; otherwise this overrides it everywhere, clamped to a solvable range. Higher = harder.
     */
    @SerialName("blank_ratio") val blankRatio: Double = 0.0,
    /** Allow the generator to use × (on tiers that otherwise would). */
    @SerialName("allow_multiply") val allowMultiply: Boolean = true,
    /** Allow the generator to use ÷ (on tiers that otherwise would). */
    @SerialName("allow_divide") val allowDivide: Boolean = true,
    /** Show the per-cell Reveal assist on the play screen. */
    @SerialName("show_reveal") val showReveal: Boolean = true,
    /** Reveals granted free each level before an ad is asked for. `0` = always ask (when an ad can be shown). */
    @SerialName("reveal_free_per_level") val revealFreePerLevel: Int = 1,
    /**
     * Require a completed rewarded ad for reveals beyond the free allowance. Only enforced when a
     * rewarded ad can actually be offered -- ad-free users and platforms without rewarded support
     * still get the reveal, just without the ad.
     */
    @SerialName("reveal_requires_ad") val revealRequiresAd: Boolean = true,
)

/** Knobs for the Rapid-Fire Riddles mode. */
@Serializable
data class RiddlesConfig(
    @SerialName("enabled") val enabled: Boolean = true,
    /** Number of levels offered per difficulty tier. */
    @SerialName("levels_per_tier") val levelsPerTier: Int = 30,
    /** Questions in each level's set. */
    @SerialName("questions_per_level") val questionsPerLevel: Int = 10,
    /** Seconds allowed per question before it counts as a miss. */
    @SerialName("per_question_seconds") val perQuestionSeconds: Int = 20,
    @SerialName("allow_arithmetic") val allowArithmetic: Boolean = true,
    @SerialName("allow_sequences") val allowSequences: Boolean = true,
    @SerialName("allow_shape_algebra") val allowShapeAlgebra: Boolean = true,
    /** Allow × in arithmetic questions (on tiers that otherwise would). */
    @SerialName("allow_multiply") val allowMultiply: Boolean = true,
    /** Allow ÷ in arithmetic questions (on tiers that otherwise would). */
    @SerialName("allow_divide") val allowDivide: Boolean = true,
    /** Include the "logic" kinds: find the missing number, word problems, think-of-a-number, rule and symbol riddles. */
    @SerialName("allow_logic") val allowLogic: Boolean = true,
    /**
     * Fraction of a set's questions that must be right to clear it (rounded up, never below one).
     * Below it the run earns no stars, unlocks nothing and doesn't count for streaks or badges.
     */
    @SerialName("pass_ratio") val passRatio: Double = 0.5,
)

/** Knobs for Brainstorm, the mixed Alphabets -> Maths -> Riddles run. */
@Serializable
data class BrainstormConfig(
    /** Show the Brainstorm card on Home. It also needs both math and riddles enabled to run. */
    @SerialName("enabled") val enabled: Boolean = true,
    /**
     * Offer an interstitial (through the shared `ads.interstitial` pacing) on the break screen
     * between games. Off by default: a Brainstorm run skips the results screens where
     * interstitials normally fire, so turning this on adds ads to a flow that had none.
     */
    @SerialName("show_interstitials") val showInterstitials: Boolean = false,
)

/**
 * How the Daily button picks its game. Each weight is a relative chance: `1/1/1` is an even mix,
 * `2/1/1` makes word crosswords twice as likely, `0` takes a game out of the Daily altogether. A
 * mode switched off in its own section, or with no level unlocked yet, is skipped whatever its
 * weight; if nothing is left, the Daily is a word crossword.
 */
@Serializable
data class DailyMixConfig(
    @SerialName("crossword_weight") val crosswordWeight: Int = 1,
    @SerialName("math_crossword_weight") val mathCrosswordWeight: Int = 1,
    @SerialName("riddles_weight") val riddlesWeight: Int = 1,
) {
    /** Negative weights count as zero; all zero falls back to word crosswords only. */
    fun sanitized(): DailyMixConfig {
        val clean = DailyMixConfig(
            crosswordWeight = crosswordWeight.coerceAtLeast(0),
            mathCrosswordWeight = mathCrosswordWeight.coerceAtLeast(0),
            riddlesWeight = riddlesWeight.coerceAtLeast(0),
        )
        return if (clean.crosswordWeight + clean.mathCrosswordWeight + clean.riddlesWeight == 0) {
            clean.copy(crosswordWeight = 1)
        } else {
            clean
        }
    }
}
